import React, { type ReactElement, type RefObject, useEffect } from 'react'
import ReactGA from 'react-ga4'
import { useLocation, useOutlet } from 'react-router'
import ScrollToTop from 'react-scroll-to-top'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import useScrollToPage from '../../../utils/useScrollToPageRef'
import useWindowDimensions from '../../hooks/useDimensions'
import Arrow from '../../icons/Arrow'
import NavBar from '../Navbar/Navbar'

const Layout = ({
  routes,
}: {
  routes: Array<{
    path: string
    element: ReactElement
    nodeRef: RefObject<HTMLDivElement>
  }>
}) => {
  const location = useLocation()
  const { nodeRef } = routes.find((route) => route.path === location.pathname) ?? {}
  const { isBottomOfThePage, goToPage } = useScrollToPage()
  const currentOutlet = useOutlet()
  const isHomePage = location.pathname === '/'
  const isBlogPage = location.pathname === '/blog' || location.pathname.startsWith('/blog/')
  // On phones a smaller button closer to the corner covers less of the content.
  const isMobile = useWindowDimensions().width < 768
  const scrollButtonSize = isMobile ? 40 : 50
  const scrollButtonInset = isMobile ? 16 : 40

  useEffect(() => {
    if (location.pathname) {
      ReactGA.send({ hitType: 'pageview', page: location.pathname })
    }
  }, [location.pathname])

  return (
    <>
      <SwitchTransition>
        <CSSTransition
          key={location.pathname}
          timeout={500}
          classNames="page"
          unmountOnExit
          // A new page opens at its top, not at the scroll position of the page it came from.
          onEnter={() => {
            if (!location.hash) window.scrollTo({ top: 0, behavior: 'instant' })
          }}
        >
          <div ref={nodeRef} className="page" style={{}}>
            {!isHomePage && <NavBar />}
            {currentOutlet}
          </div>
        </CSSTransition>
      </SwitchTransition>
      {!isBlogPage && (
        <ScrollToTop
          smooth
          color="white"
          top={500}
          style={{
            background: '#EC5922',
            borderRadius: '50%',
            height: scrollButtonSize,
            width: scrollButtonSize,
            transition: 'all 0.3s',
            right: scrollButtonInset,
            // Leaves room for the next-page arrow, which the homepage does not show.
            bottom: isBottomOfThePage && !isHomePage ? 140 : scrollButtonInset,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        />
      )}
      {isBottomOfThePage && !isBlogPage && !isHomePage && (
        <div
          onClick={() => {
            goToPage()
          }}
          style={{
            background: '#EC5922',
            borderRadius: '50%',
            height: scrollButtonSize,
            width: scrollButtonSize,
            transition: 'all 0.3s',
            bottom: scrollButtonInset,
            position: 'fixed',
            right: scrollButtonInset,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Arrow
            customStyle={{
              transform: 'rotate(-180deg)',
              boxShadow: '0 9px 25px 0 rgba(132, 128, 177, 0.28)',
            }}
          />
        </div>
      )}
    </>
  )
}

export default Layout
